import React, { useEffect, useState, useRef } from 'react';
import { FaBell, FaPhoneAlt, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  message: string;
  type: string; 
  lead: { 
      id: string;
      firstName: string;
      lastName: string;
      mobile: string;
  }; 
}

// Configuration
const IS_PROD = window.location.hostname !== 'localhost';
const BASE_URL = IS_PROD 
    // ? 'https://ayushvatika.com.api' 
    // : 'http://localhost:8484';

// --- HELPER FUNCTIONS (Session Storage) ---
// Ye check karega ki kya ye ID browser ki memory me pehle se hai?
const isNotificationSeen = (id: string) => {
    try {
        const seen = JSON.parse(sessionStorage.getItem('seen_popup_ids') || '[]');
        return seen.includes(id);
    } catch { return false; }
};

// Ye ID ko browser ki memory me save kar dega taaki dobara popup na aaye
const markNotificationAsSeenLocally = (id: string) => {
    try {
        const seen = JSON.parse(sessionStorage.getItem('seen_popup_ids') || '[]');
        if (!seen.includes(id)) {
            seen.push(id);
            sessionStorage.setItem('seen_popup_ids', JSON.stringify(seen));
        }
    } catch (e) { console.error("Session storage error", e); }
};

const getUsernameFromToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload).sub;
    } catch (error) { return null; }
};

const GlobalReminderPopup: React.FC = () => {
  const [activeNotification, setActiveNotification] = useState<Notification | null>(null);
  const navigate = useNavigate();
  
  // Ref for lock mechanism (Prevent double opening)
  const isPopupOpen = useRef(false); 
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio('/alert.mp3'); 
  }, []);

  const checkForNotifications = async () => {
    // 1. Agar popup already screen par hai, to naya fetch mat karo
    if (isPopupOpen.current) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    let url = '';
    const userId = localStorage.getItem('userId');
    const timestamp = new Date().getTime();

    // URL Construction
    if (userId) {
        url = `${BASE_URL}/api/notifications/unread/${userId}?t=${timestamp}`;
    } else {
        const username = getUsernameFromToken(token);
        if (username) {
            url = `${BASE_URL}/api/notifications/unread-by-user/${username}?t=${timestamp}`;
        } else { return; }
    }

    try {
      const res = await fetch(url, {
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache', 
            'Pragma': 'no-cache'
        }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.length > 0) {
            // ✅ MAIN FIX: SessionStorage check karein instead of useRef
            // "Wo notification dhundo jo abhi tak SEEN nahi hua hai"
            const unseenNotification = data.find((n: any) => !isNotificationSeen(n.id));

            if (unseenNotification) {
                console.log("🔔 New Popup Triggered:", unseenNotification);
                
                // ✅ FIX: Turant browser storage mein daal do
                // Isse page refresh hone par bhi ye yaad rahega
                markNotificationAsSeenLocally(unseenNotification.id);

                setActiveNotification(unseenNotification);
                isPopupOpen.current = true; 
                
                // Audio Logic
                if(audioRef.current) {
                    const playPromise = audioRef.current.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(error => {
                            console.log("Audio autoplay prevented by browser policy.");
                        });
                    }
                }
            }
        }
      }
    } catch (error) {
      console.error("Error polling notifications:", error);
    }
  };

  useEffect(() => {
    checkForNotifications(); // Initial check

    const intervalId = setInterval(() => {
       checkForNotifications(); 
    }, 10000); // 10 Sec Loop

    return () => clearInterval(intervalId);
  }, []);

  // --- ACTIONS ---

  const closePopup = () => {
    // ID save karne ki zarurat yaha nahi hai kyunki humne upar 'checkForNotifications' me hi save kar liya hai
    isPopupOpen.current = false;         
    setActiveNotification(null);         
  };

  const handleAcknowledge = async () => {
    if (!activeNotification) return;
    
    const notifId = activeNotification.id;
    closePopup();

    // Background me server ko bata do
    await markAsReadOnServer(notifId);
  };

  const handleViewLead = async () => {
    if (!activeNotification) return;

    const notifId = activeNotification.id;
    const leadId = activeNotification.lead.id;
   
    closePopup(); // UI Close First
    
    // Server Call
    markAsReadOnServer(notifId);
    
    navigate(`/admin/leads/sales/${leadId}`);
  };

  const markAsReadOnServer = async (id: string) => {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${BASE_URL}/api/notifications/${id}/read`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log("✅ Marked as read on server:", id);
      } catch(e) { console.error("Failed to mark as read", e); }
  }

  if (!activeNotification) return null;

  const isUrgent = activeNotification.type === '5M_REMINDER';
  const bgColor = isUrgent ? 'bg-red-600' : 'bg-blue-600';
  const borderColor = isUrgent ? 'border-red-500' : 'border-blue-500';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm animate-fade-in">
      <div className={`bg-gray-900 rounded-2xl shadow-2xl border-2 ${borderColor} w-full max-w-lg overflow-hidden transform transition-all scale-100`}>
        
        {/* Header */}
        <div className={`${bgColor} p-4 flex justify-between items-center`}>
            <h3 className="text-white font-bold text-xl flex items-center animate-pulse">
                <FaBell className="mr-2" /> 
                {isUrgent ? 'URGENT CALL REMINDER' : 'Scheduled Call Alert'}
            </h3>
            <button onClick={handleAcknowledge} className="text-white hover:text-gray-200">
                <FaTimes size={24} />
            </button>
        </div>

        {/* Body */}
        <div className="p-8 text-center">
            <div className="mb-6 flex justify-center">
                <div className={`h-24 w-24 ${isUrgent ? 'bg-red-900' : 'bg-blue-900'} rounded-full flex items-center justify-center border-4 border-white shadow-lg`}>
                     <FaPhoneAlt className="text-white text-4xl animate-bounce" />
                </div>
            </div>
            
            <h4 className="text-3xl font-bold text-white mb-2">
                {activeNotification.lead.firstName} {activeNotification.lead.lastName}
            </h4>
            
            <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 mb-8 shadow-inner">
                <p className="text-lg text-gray-200 font-medium leading-relaxed">
                    {activeNotification.message}
                </p>
            </div>
            
            <div className="flex gap-4 justify-center">
                <button 
                    onClick={handleAcknowledge}
                    className="px-8 py-3 rounded-xl border border-gray-500 text-white hover:bg-gray-700 font-bold text-lg transition-all"
                >
                    OK, Got it
                </button>

                <button 
                    onClick={handleViewLead}
                    className="px-8 py-3 rounded-xl bg-green-600 text-white hover:bg-green-500 font-bold text-lg shadow-lg hover:shadow-green-500/50 transition-all flex items-center"
                >
                    <FaPhoneAlt className="mr-2" /> View Lead
                </button>
            </div>
        </div>
    
        <div className="bg-gray-950 p-2 text-center">
            <p className="text-xs text-gray-500">This notification will remain in your navbar history.</p>
        </div>
      </div>
    </div>
  );
};

export default GlobalReminderPopup;